<template>
  <div
    v-if="plagiarismFeedbackData.length > 0"
    class="flex flex-col h-full content-start space-y-5"
  >
    <div class="flex flex-col space-y-5 flex-1 w-full">
      <div class="w-full flex justify-between items-end">
        <div class="flex space-x-1 items-center">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <Icon v-bind="props" icon="ant-design:info-circle" class="text-xl cursor-pointer" />
            </template>
            <div class="w-40 whitespace-normal text-center">
              Check for any plagiarized text. Then, use the other provided tools to rewrite the text
              in your own words.
            </div>
          </v-tooltip>
          <span class="font-semibold text-sm 2xl:text-base">Select a card</span>
        </div>
      </div>

      <div class="w-full flex justify-center mb-5">
        <div class="grid place-items-center content-center text-center">
          <v-progress-circular
            bg-color="rgba(255, 255, 255, 1)"
            :width="7"
            :size="120"
            :model-value="totalPlagiarism"
            :color="getColor"
            ><span class="text-2xl font-semibold">{{ totalPlagiarism }}%</span></v-progress-circular
          >
        </div>
      </div>

      <PlagiarismCheckerDropdown
        v-for="item in plagiarismFeedbackData"
        :link="item.url"
        :source="item.title"
        :key="item.id"
        :is-open="selectedItem === item.id"
        :elemid="item.id"
        @toggleDropdown="() => onSelectedItemChange(item.id)"
        v-on:card-click="selectCardSentence(item.id, item.selectionRange)"
        @click="scrollToElement('.ww-sentence-id' + item.id)"
      />
    </div>
  </div>
  <div v-else class="flex min-h-[70vh] flex-col space-y-6 items-center justify-center">
    <div v-if="loadingBrainResponse" class="flex flex-col space-y-6 items-center">
      <p class="text-xl leading-loose text-center text-black text-opacity-90">
        Loading suggestions
      </p>
    </div>
    <div v-if="!plagiarismFeedbackData?.length && !loadingBrainResponse">
      <div v-if="functionError">
        <p class="text-xl leading-loose text-center text-black text-opacity-90">
          An error was encountered while running the analysis
        </p>
      </div>
      <div v-else class="flex flex-col space-y-6 items-center justify-center">
        <Icon :icon="'ant-design:check-circle-filled'" color="#52C41A" :height="80" />
        <p class="text-xl leading-loose text-center text-black text-opacity-90">
          Great work!<br />No suggestions found!
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import PlagiarismCheckerDropdown from '@/components/home/plagiarismChecker/PlagiarismCheckerDropdown.vue';
import { Icon } from '@iconify/vue';
import { Vue, Options } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { usePayloadStore } from '@/stores/payload';

@Options({
  components: {
    Icon,
    PlagiarismCheckerDropdown
  }
})
export default class PlagiarismChecker extends Vue {
  @Prop({ required: true })
  private quillEditor!: any;

  @Prop()
  public plagiarismFeedbackData: any;

  @Prop()
  public totalPlagiarism: any;

  @Prop()
  public highlightedCard: any;
  private payloadStore = usePayloadStore();
  public selectedItem = null;
  public loadingBrainResponse = this.payloadStore.payload.loadingBrainResponse;
  public functionError = this.payloadStore.payload.functionError;

  @Watch('payloadStore.payload.functionError')
  public onFunctionError() {
    this.functionError = this.payloadStore.payload.functionError;
  }

  get getColor() {
    if (Number(this.totalPlagiarism) > 75) {
      return '#d50000d9';
    } else if (Number(this.totalPlagiarism) > 25) {
      return '#ffc107';
    } else {
      return '#52c41ad9';
    }
  }

  @Watch('payloadStore.payload.loadingBrainResponse')
  public onLoadingResponse() {
    this.loadingBrainResponse = this.payloadStore.payload.loadingBrainResponse;
  }

  @Watch('highlightedCard')
  public isHighlightedCard() {
    if (this.highlightedCard == this.selectedItem) this.selectedItem = null;
    else this.selectedItem = this.highlightedCard;
  }

  public onSelectedItemChange(val: any) {
    if (val === this.selectedItem) this.selectedItem = null;
    else this.selectedItem = val;
  }

  public selectCardSentence(id: any, selectionRange: any) {
    this.$emit('selectCardSentence', id, selectionRange);
  }

   public scrollToElement(id: any) {
    const elements = this.quillEditor.root.querySelector(`${id}`);
    if (elements) {
      elements.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }
}
</script>

<style scoped>
.good,
.good + span {
  color: #52c41ad9 !important;
  border-color: #52c41ad9 !important;
}

.bad,
.bad + span {
  color: #d50000d9 !important;
  border-color: #d50000d9 !important;
}

.average,
.average + span {
  color: #ffc107 !important;
  border-color: #ffc107 !important;
}
</style>
